import { useSyncExternalStore } from "react";
import type { Stop } from "./plannerLogic";

/**
 * The visitor's route map: the open tab, the ready-made route they are looking at, and the route they are building. Kept outside
 * React so the hub's hero button and "Build my route" banner can open a tab from where they are.
 *
 * In memory only, and gone when the section leaves the page (`resetRouteMapState`): My route is empty on every visit, with no date, as
 * Ilse's v29 has it. Nothing is restored from sessionStorage or localStorage any more (the first version kept it per tab).
 */

export type RouteMapTab = "routes" | "mine";

/** How far either side of the start date is fine, in days: the calendar's "Exact date / ± 1 day / ± 3 days / ± 1 week" */
export type DateFlex = 0 | 1 | 3 | 7;

export interface RouteMapState {
	tab: RouteMapTab;
	/** the ready-made route that is open on the Routes tab */
	selectedRouteId: string | null;
	/** my route: a ready-made one copied by "Customise", or one built stop by stop */
	stops: Stop[];
	/** the ready-made route my route was copied from, for as long as it is unchanged: any edit clears it */
	sourceRouteId: string | null;
	/** `YYYY-MM-DD`; null until the visitor picks one */
	startDate: string | null;
	flex: DateFlex;
}

export const DEFAULT_STATE: RouteMapState = { tab: "routes", selectedRouteId: null, stops: [], sourceRouteId: null, startDate: null, flex: 0 };

export const ROUTE_MAP_ID = "route-map";

interface Store {
	subscribe: (listener: () => void) => () => void;
	getSnapshot: () => RouteMapState;
	set: (patch: Partial<RouteMapState>) => void;
	reset: () => void;
}

function createStore(): Store {
	const listeners = new Set<() => void>();
	let current: RouteMapState = DEFAULT_STATE;
	const emit = () => listeners.forEach((listener) => listener());
	return {
		subscribe: (listener) => {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		},
		getSnapshot: () => current,
		set: (patch) => {
			current = { ...current, ...patch };
			emit();
		},
		reset: () => {
			current = DEFAULT_STATE;
			emit();
		},
	};
}

const stores = new Map<string, Store>();
/** the store of the hub that is on screen: what the setters below act on */
let active: Store | null = null;

const getServerSnapshot = (): RouteMapState => DEFAULT_STATE;

export function useRouteMapState(countrySlug: string): RouteMapState {
	let store = stores.get(countrySlug);
	if (!store) {
		store = createStore();
		stores.set(countrySlug, store);
	}
	active = store;
	return useSyncExternalStore(store.subscribe, store.getSnapshot, getServerSnapshot);
}

/** What the hub's hero button and "Build my route" banner call, from outside the section. */
export const openRouteMapTab = (tab: RouteMapTab): void => active?.set({ tab });
export const setSelectedRoute = (selectedRouteId: string | null): void => active?.set({ selectedRouteId });
/** Every edit of my route goes through here, so the route is no longer its source's unless the caller says it still is. */
export const setMyStops = (stops: Stop[], sourceRouteId: string | null = null): void => active?.set({ stops, sourceRouteId });
export const setStartDate = (startDate: string | null): void => active?.set({ startDate });
export const setFlex = (flex: DateFlex): void => active?.set({ flex });
/** The state as it is now, for what has to read it outside React (tests, a handler) */
export const currentRouteMapState = (): RouteMapState => active?.getSnapshot() ?? DEFAULT_STATE;
/** The section left the page: the next visit starts empty. */
export const resetRouteMapState = (countrySlug: string): void => stores.get(countrySlug)?.reset();

export const scrollToRouteMap = (): void => {
	document.getElementById(ROUTE_MAP_ID)?.scrollIntoView({ behavior: "smooth", block: "start" });
};
